import axios from 'axios';
import { API_ENDPOINTS, axiosConfig } from '../config/api';
import { setTokens, getAccessToken, getRefreshToken, clearAuth, setUserInfo } from '../utils/auth';
import { apiClient } from './api/apiClient';

// Use centralized apiClient with consistent token refresh handling
const api = apiClient;

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  access_token?: string;
  refresh_token?: string;
  token_type?: string;
  expires_in?: number;
  user_id?: number;
  username?: string;
  email?: string;
  name?: string;
  role?: string;
}

export interface LogoutRequest {
  refresh_token: string;
}

export interface LogoutResponse {
  success: boolean;
  message: string;
}

export interface ProfileData {
  id: number;
  /** 로그인 아이디 — 이메일과 분리된 별도 식별자, 본인은 수정 불가(관리자만). */
  username: string;
  email: string;
  name: string;
  role: string;
  /** 백엔드가 빌드해서 보내는 안정 경로. 없으면 이니셜 fallback 표시. */
  profile_image_url?: string;
  /** @멘션 메일 수신 여부 — 프로필에서 본인이 토글. */
  email_notifications_enabled: boolean;
  created_at: string;
}

export interface ProfileResponse {
  success: boolean;
  message?: string;
  data?: ProfileData;
}

export interface UpdateProfileResponse extends ProfileResponse {
  access_token?: string;
  refresh_token?: string;
}

export interface UserDirectoryItem {
  id: number;
  name: string;
  username: string;
  email: string;
  role: string;
  /** 작성자 칩에 아바타 표시용. 없으면 이니셜 fallback. */
  profile_image_url?: string;
}

export interface UsersResponse {
  success: boolean;
  message?: string;
  data: UserDirectoryItem[];
}

export class AuthService {
  /**
   * Login user and store tokens
   */
  static async login(data: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await api.post<LoginResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        data
      );

      if (response.data.success && response.data.access_token && response.data.refresh_token) {
        // Store tokens
        setTokens(response.data.access_token, response.data.refresh_token);

        // Store user info
        if (response.data.user_id && response.data.username && response.data.email && response.data.name) {
          setUserInfo({
            user_id: response.data.user_id,
            username: response.data.username,
            email: response.data.email,
            name: response.data.name,
            role: response.data.role,
          });
        }
      }

      return response.data;
    } catch (error: any) {
      console.error('로그인 실패:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Logout user and clear tokens
   */
  static async logout(): Promise<void> {
    const refreshToken = getRefreshToken();

    try {
      if (refreshToken) {
        await api.post<LogoutResponse>(API_ENDPOINTS.AUTH.LOGOUT, {
          refresh_token: refreshToken,
        });
      }
    } catch (error) {
      console.error('로그아웃 요청 실패:', error);
    } finally {
      // Always clear local storage
      clearAuth();
    }
  }

  /**
   * Fetch the user directory (id/name/email/role only) — autocomplete picker용.
   */
  static async listUsers(): Promise<UserDirectoryItem[]> {
    try {
      const response = await api.get<UsersResponse>(API_ENDPOINTS.AUTH.USERS);
      return response.data.success ? response.data.data : [];
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 신규 유저 생성 — admin 전용. role 은 생성 시점에 지정.
   * 백엔드 POST /v0.1/auth/register (RequireRole admin).
   */
  static async registerUser(data: {
    username: string;
    email: string;
    password: string;
    name: string;
    role: string;
  }): Promise<UserDirectoryItem> {
    try {
      const response = await api.post<{ success: boolean; message?: string; data?: UserDirectoryItem }>(
        API_ENDPOINTS.AUTH.REGISTER,
        data,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || '유저 생성 실패');
      }
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 유저 권한 변경 — admin 전용. 본인 권한은 변경 불가(백엔드 가드).
   * 백엔드 PATCH /v0.1/auth/users/:id/role.
   */
  static async changeUserRole(id: number, role: string): Promise<UserDirectoryItem> {
    try {
      const response = await api.patch<{ success: boolean; message?: string; data?: UserDirectoryItem }>(
        API_ENDPOINTS.AUTH.USER_ROLE(id),
        { role },
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || '권한 변경 실패');
      }
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 유저 정보 변경(아이디/이메일/비밀번호) — admin 전용.
   * 보낸 필드만 변경한다. 백엔드 PATCH /v0.1/auth/users/:id.
   */
  static async updateUser(
    id: number,
    data: { username?: string; email?: string; password?: string },
  ): Promise<UserDirectoryItem> {
    try {
      const response = await api.patch<{ success: boolean; message?: string; data?: UserDirectoryItem }>(
        API_ENDPOINTS.AUTH.USER(id),
        data,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.message || '유저 정보 변경 실패');
      }
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 유저 삭제(soft delete) — admin 전용. 본인 삭제 불가(백엔드 가드).
   * 백엔드 DELETE /v0.1/auth/users/:id.
   */
  static async deleteUser(id: number): Promise<void> {
    try {
      const response = await api.delete<{ success: boolean; message?: string }>(
        API_ENDPOINTS.AUTH.USER(id),
      );
      if (!response.data.success) {
        throw new Error(response.data.message || '유저 삭제 실패');
      }
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * Fetch current user profile from server (truth source for role/name).
   */
  static async getProfile(): Promise<ProfileResponse> {
    try {
      const response = await api.get<ProfileResponse>(API_ENDPOINTS.AUTH.ME);
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update editable profile fields (`name` + 이메일 알림 설정).
   * 서버가 새 토큰을 발급하면 localStorage 와 user_info 를 동기화한다.
   */
  static async updateProfile(
    name: string,
    emailNotificationsEnabled?: boolean,
    email?: string,
  ): Promise<UpdateProfileResponse> {
    try {
      const body: { name: string; email?: string; email_notifications_enabled?: boolean } = { name };
      if (email !== undefined && email !== '') {
        body.email = email;
      }
      if (emailNotificationsEnabled !== undefined) {
        body.email_notifications_enabled = emailNotificationsEnabled;
      }
      const response = await api.patch<UpdateProfileResponse>(API_ENDPOINTS.AUTH.ME, body);
      if (response.data.success) {
        if (response.data.access_token && response.data.refresh_token) {
          setTokens(response.data.access_token, response.data.refresh_token);
        }
        if (response.data.data) {
          setUserInfo({
            user_id: response.data.data.id,
            username: response.data.data.username,
            email: response.data.data.email,
            name: response.data.data.name,
            role: response.data.data.role,
          });
        }
      }
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 비밀번호 변경 — 기존 비번 일치 시 새 비번으로 교체.
   * 서버는 별도 토큰 갱신 없이 success/message 만 반환 (JWT claim 에 password
   * 가 없어 강제 재로그인 불필요).
   */
  static async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.post<{ success: boolean; message?: string }>(
        `${API_ENDPOINTS.AUTH.ME}/password`,
        { current_password: currentPassword, new_password: newPassword },
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * MCP(Claude Code) 연결용 토큰 발급 — 웹 로그인 토큰과 따로 90일 쓴다(로그아웃해도 유지).
   */
  static async issueMcpToken(): Promise<{ token: string; expires_at: string }> {
    try {
      const response = await api.post<{ success: boolean; data: { token: string; expires_at: string } }>(
        `${API_ENDPOINTS.AUTH.ME}/mcp-token`,
      );
      return response.data.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 프로필 이미지 업로드 — multipart 형식. 5MB / image/* 제한은 백엔드에서 검증.
   * 성공 시 새로운 profile_image_url 반환 — caller 가 이걸로 컨텍스트/캐시 갱신.
   */
  static async uploadProfileImage(file: File): Promise<{ success: boolean; message?: string; profile_image_url?: string }> {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await api.post<{ success: boolean; message?: string; profile_image_url?: string }>(
        `${API_ENDPOINTS.AUTH.ME}/profile-image`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * 프로필 이미지 삭제. 사용자에게 이미지가 없으면 404 (백엔드 응답).
   */
  static async deleteProfileImage(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await api.delete<{ success: boolean; message?: string }>(
        `${API_ENDPOINTS.AUTH.ME}/profile-image`,
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data || error;
    }
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<LoginResponse> {
    // access_token 은 서버에서 검증에 쓰이지 않음 — 자동 로그인 복원처럼
    // access token 이 없는 상태에서도 refresh token 만으로 재발급 가능.
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post<LoginResponse>(
        `${axiosConfig.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`,
        {
          access_token: accessToken ?? '',
          refresh_token: refreshToken,
        }
      );

      if (response.data.success && response.data.access_token && response.data.refresh_token) {
        setTokens(response.data.access_token, response.data.refresh_token);
      }

      return response.data;
    } catch (error: any) {
      // Error logged internally by apiClient
      clearAuth();
      throw error.response?.data || error;
    }
  }
}
